import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from "../../../../AuthContext/AuthContext";
import { Helper } from "../../../../tools/Helper";
import { api_Routes } from "../../../../api_Routes";
import { useCenteredSnackbar } from "../../../../tools/useCenteredSnackbar";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    LinearProgress,
    Button,
    Avatar,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tabs,
    Tab,
    CircularProgress,
} from '@mui/material';
import {
    Receipt,
    Payment,
    CalendarToday,
    Description,
    ExpandMore,
    Image,
    PushPin,
    EmojiEvents,
    SupportAgent,
    Security,
    ConfirmationNumber,
    ViewCarousel,
    BarChart,
    Devices,
    Public,
    Timeline,
    Link as LinkIcon,
    Settings,
} from '@mui/icons-material';
import {useNavigate} from "react-router";

const OfferDetails = () => {
    const { auth } = useAuth();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [offerData, setOfferData] = useState(null);
    const [expandedService, setExpandedService] = useState(null);
    const [statisticsTab, setStatisticsTab] = useState(0);
    const [loadingStatistics, setLoadingStatistics] = useState({});
    const [servicesStatistics, setServicesStatistics] = useState({});
    const { showCenteredPopup } = useCenteredSnackbar();
    const navigate = useNavigate();

    const fetchOfferData = async () => {
        setIsLoading(true);
        const { response, message } = await Helper.Get({
            url: `${api_Routes.dashboard.orderhistory.offerDetails}${id}`,
            hasToken: true,
        });

        if (response) {
            setOfferData(response.data);
        } else {
            showCenteredPopup(message || "Failed to fetch offer data", {
                variant: "error",
                persist: true
            });
        }
        setIsLoading(false);
    };

    const fetchServiceStatistics = async (service, requestId) => {
        if (!service.service_data) return;

        const serviceKey = `${service.service_data.type}_${service.service_data.service_id}`;

        // Don't reload if already loaded
        if (servicesStatistics[serviceKey]) return;

        setLoadingStatistics(prev => ({ ...prev, [serviceKey]: true }));

        // Use GET request with data parameter as per your Helper.js
        const { response, message } = await Helper.Get({
            url: `${api_Routes.dashboard.orderhistory.serviceStatistics}`,
            hasToken: true,
            data: {
                service_type: service.service_data.type,
                service_id: service.service_data.service_id,
                request_id: requestId
            }
        });

        if (response) {
            setServicesStatistics(prev => ({
                ...prev,
                [serviceKey]: response.data
            }));
        } else {
            showCenteredPopup(message || "Failed to fetch statistics", {
                variant: "error"
            });
        }

        setLoadingStatistics(prev => ({ ...prev, [serviceKey]: false }));
    };

    const handleServiceExpand = (serviceIndex, service, requestId) => {
        const newExpandedService = expandedService === serviceIndex ? null : serviceIndex;
        setExpandedService(newExpandedService);

        // Load statistics when service is expanded
        if (newExpandedService !== null && service.service_data) {
            fetchServiceStatistics(service, requestId);
        }
    };

    useEffect(() => {
        fetchOfferData();
    }, [id]);

    const getStatusChip = (status) => {
        const statusConfig = {
            pending: { color: 'warning', label: 'Pending' },
            paid: { color: 'success', label: 'Paid' },
            new_request: { color: 'info', label: 'New Request' },
            in_progress: { color: 'primary', label: 'In Progress' },
            complete: { color: 'success', label: 'Completed' },
            active: { color: 'success', label: 'Active' },
        };

        const config = statusConfig[status] || { color: 'default', label: status };
        return (
            <Chip
                label={config.label}
                color={config.color}
                variant="filled"
                size="small"
            />
        );
    };

    const getServiceIcon = (serviceId) => {
        const iconMap = {
            1: <ViewCarousel />,
            4: <ConfirmationNumber />, // Bulk-Ticket
            9: <ViewCarousel />,
            10: <PushPin />, // Pin Up
            11: <PushPin />, // Pin Down
        };
        return iconMap[serviceId] || <Settings />;
    };

    const getServiceDetails = (service) => {
        if (!service.service_data) return [];

        const { type, data } = service.service_data;

        switch (type) {
            case 'pin':
                return [
                    { label: 'Pin URL', value: data.pin_url, isLink: true },
                    { label: 'Pin Place', value: data.pin_place },
                    { label: 'Subscription Days', value: data.count_days_subscriptions },
                    { label: 'Start Date', value: new Date(data.date_start_active).toLocaleDateString() },
                ];
            case 'banner':
                return [
                    { label: 'Start Date', value: new Date(data.date_start_active).toLocaleDateString() },
                    { label: 'Subscription Days', value: data.count_days_subscriptions },
                    { label: 'Status', value: data.status },
                ];
            case 'top_result':
                return [
                    { label: 'Start Date', value: new Date(data.date_start_active).toLocaleDateString() },
                    { label: 'Subscription Days', value: data.count_days_subscriptions },
                    { label: 'Status', value: data.status },
                ];
            case 'tickets_ads':
                const completionRate = data.count_ticket_subscriptions > 0
                    ? ((data.count_ticket_send / data.count_ticket_subscriptions) * 100).toFixed(1)
                    : 0;
                return [
                    { label: 'Subject', value: data.subject },
                    { label: 'Total Tickets', value: data.count_ticket_subscriptions },
                    { label: 'Tickets Sent', value: data.count_ticket_send },
                    { label: 'Completion Rate', value: `${completionRate}%` },
                    { label: 'Start Date', value: new Date(data.date_start_active).toLocaleDateString() },
                    { label: 'Subscription Days', value: data.count_days_subscriptions },
                    { label: 'Status', value: data.status },
                ];
            case 'trust':
                return [
                    { label: 'Start Date', value: new Date(data.date_start_active).toLocaleDateString() },
                    { label: 'Subscription Days', value: data.count_days_subscriptions },
                    { label: 'Status', value: data.status },
                ];
            default:
                return [];
        }
    };

    const renderServiceStatistics = (service) => {
        const serviceKey = `${service.service_data.type}_${service.service_data.service_id}`;
        const statistics = servicesStatistics[serviceKey];
        const isLoading = loadingStatistics[serviceKey];

        if (isLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Loading statistics...</Typography>
                </Box>
            );
        }

        if (!statistics) {
            return (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                    <Typography color="text.secondary">
                        Statistics will load when expanded
                    </Typography>
                    <Button
                        variant="outlined"
                        sx={{ mt: 1 }}
                        onClick={() => fetchServiceStatistics(service, offerData.request_id)}
                    >
                        Load Statistics
                    </Button>
                </Box>
            );
        }

        if (statistics.error) {
            return (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                    <Typography color="error">
                        {statistics.error}
                    </Typography>
                    <Button
                        variant="outlined"
                        sx={{ mt: 1 }}
                        onClick={() => fetchServiceStatistics(service, offerData.request_id)}
                    >
                        Retry
                    </Button>
                </Box>
            );
        }

        // Ticket Ads Statistics
        if (service.service_data?.type === 'tickets_ads') {
            return renderTicketAdsStatistics(service.service_data.data);
        }

        // Regular Service Statistics (Pin, Banner, TopResult)
        return renderRegularStatistics(statistics);
    };

    const renderTicketAdsStatistics = (ticketData) => {
        const completionRate = ticketData.count_ticket_subscriptions > 0
            ? ((ticketData.count_ticket_send / ticketData.count_ticket_subscriptions) * 100).toFixed(1)
            : 0;

        return (
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                <ConfirmationNumber sx={{ mr: 1 }} />
                                Ticket Campaign Performance
                            </Typography>
                            <Divider sx={{ mb: 3 }} />

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={3}>
                                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                                        <ConfirmationNumber sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                                        <Typography variant="h4" color="primary" fontWeight="bold">
                                            {ticketData.count_ticket_subscriptions || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Tickets
                                        </Typography>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: '#e8f5e8' }}>
                                        <Typography variant="h4" color="success.main" fontWeight="bold">
                                            {ticketData.count_ticket_send || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Tickets Sent
                                        </Typography>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: '#fff3e0' }}>
                                        <Typography variant="h4" color="warning.main" fontWeight="bold">
                                            {completionRate}%
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Completion Rate
                                        </Typography>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: '#fce4ec' }}>
                                        <Typography variant="h4" color="secondary" fontWeight="bold">
                                            {(ticketData.count_ticket_subscriptions - ticketData.count_ticket_send) || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Remaining Tickets
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Progress Bar */}
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Campaign Progress: {completionRate}%
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={completionRate}
                                    sx={{ height: 10, borderRadius: 5 }}
                                    color={completionRate >= 100 ? "success" : completionRate >= 50 ? "primary" : "warning"}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    };

    const renderRegularStatistics = (statistics) => {
        return (
            <Box>
                <Card sx={{ mb: 3 }}>
                    <Tabs value={statisticsTab} onChange={(e, newValue) => setStatisticsTab(newValue)} centered>
                        <Tab label="Overview" />
                        <Tab label="Devices" />
                        <Tab label="Geography" />
                    </Tabs>
                </Card>

                {statisticsTab === 0 && (
                    <Grid container spacing={3}>
                        {/* Overall Statistics */}
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                        <BarChart sx={{ mr: 1 }} />
                                        Performance Overview
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />

                                    <Grid container spacing={2}>
                                        <Grid item xs={6} md={3}>
                                            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                                                <Typography variant="h4" color="primary" fontWeight="bold">
                                                    {statistics.overall_statistics?.total_views || 0}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Total Views
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e8' }}>
                                                <Typography variant="h4" color="success.main" fontWeight="bold">
                                                    {statistics.overall_statistics?.total_clicks || 0}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Total Clicks
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>
                                                <Typography variant="h4" color="warning.main" fontWeight="bold">
                                                    {statistics.overall_statistics?.total_reply || 0}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Total Replies
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#fce4ec' }}>
                                                <Typography variant="h4" color="secondary" fontWeight="bold">
                                                    {statistics.overall_statistics?.total_clicks_and_views || 0}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Total Interactions
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {statisticsTab === 1 && statistics.statisticsUsedDevices && (
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Devices sx={{ mr: 1 }} />
                                        Device Distribution
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />

                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Computer: {statistics.statisticsUsedDevices.percentage_computer?.toFixed(1) || 0}%
                                        </Typography>
                                        <LinearProgress
                                            variant="determinate"
                                            value={statistics.statisticsUsedDevices.percentage_computer || 0}
                                            sx={{ height: 10, borderRadius: 5, mb: 2 }}
                                        />

                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Phone: {statistics.statisticsUsedDevices.percentage_phone?.toFixed(1) || 0}%
                                        </Typography>
                                        <LinearProgress
                                            variant="determinate"
                                            value={statistics.statisticsUsedDevices.percentage_phone || 0}
                                            sx={{ height: 10, borderRadius: 5, mb: 2 }}
                                            color="success"
                                        />

                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Other: {statistics.statisticsUsedDevices.percentage_other?.toFixed(1) || 0}%
                                        </Typography>
                                        <LinearProgress
                                            variant="determinate"
                                            value={statistics.statisticsUsedDevices.percentage_other || 0}
                                            sx={{ height: 10, borderRadius: 5 }}
                                            color="secondary"
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {statisticsTab === 2 && statistics.countryStatistics && (
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Public sx={{ mr: 1 }} />
                                        Geographic Distribution
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />

                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Country</TableCell>
                                                    <TableCell align="right">Percentage</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {statistics.countryStatistics.map((country, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>{country.country || 'Unknown'}</TableCell>
                                                        <TableCell align="right">
                                                            <Chip
                                                                label={`${country.percentage}%`}
                                                                size="small"
                                                                color="primary"
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}
            </Box>
        );
    };

    const renderServiceInformation = (service) => {
        return (
            <Grid container spacing={3}>
                {/* Service Basic Information */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                <Settings sx={{ mr: 1 }} />
                                Service Information
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <List dense>
                                <ListItem>
                                    <ListItemText
                                        primary="Service Name"
                                        secondary={service.our_service_name || 'N/A'}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Title"
                                        secondary={service.title || 'No title provided'}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Description"
                                        secondary={service.description_offer || service.service_data.data.description || 'No description'}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Status"
                                        secondary={
                                            <Chip
                                                label={service.status}
                                                size="small"
                                                color={service.status === 'active' || service.status === 'complete' ? 'success' : 'primary'}
                                            />
                                        }
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Service Logo and Additional Info */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Media & Additional Information
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <Box sx={{ textAlign: 'center' }}>
                                {service.logo ? (
                                    <>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Service Logo
                                        </Typography>
                                        <img
                                            src={service.logo}
                                            alt="Service Logo"
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: 200,
                                                borderRadius: 8,
                                                border: '1px solid #ddd',
                                                marginBottom: 16
                                            }}
                                        />
                                    </>
                                ) : (
                                    <Box sx={{ p: 3, textAlign: 'center' }}>
                                        <Image sx={{ fontSize: 60, color: 'text.secondary', mb: 1 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            No logo available
                                        </Typography>
                                    </Box>
                                )}

                                {service.customLogo && service.customLogo.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Custom Logos ({service.customLogo.length})
                                        </Typography>
                                        <Grid container spacing={1}>
                                            {service.customLogo.map((logo, index) => (
                                                <Grid item xs={4} key={index}>
                                                    <img
                                                        src={logo}
                                                        alt={`Custom Logo ${index + 1}`}
                                                        style={{
                                                            width: '100%',
                                                            height: 60,
                                                            objectFit: 'cover',
                                                            borderRadius: 4,
                                                            border: '1px solid #ddd'
                                                        }}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    };

    const renderCustomOffer = () => {
        if (!offerData.custom_offer_details) return null;

        return (
            <Grid container spacing={3}>
                {/* Custom Offer Information */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                <Receipt sx={{ mr: 1 }} />
                                Custom Offer Information
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <List dense>
                                <ListItem>
                                    <ListItemText
                                        primary="Title"
                                        secondary={offerData.title || 'No title'}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Description"
                                        secondary={offerData.description || 'No description'}
                                    />
                                </ListItem>
                                {offerData.panel_url && (
                                    <ListItem>
                                        <ListItemIcon>
                                            <LinkIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Panel URL"
                                            secondary={
                                                <a href={offerData.panel_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                                                    {offerData.panel_url}
                                                </a>
                                            }
                                        />
                                    </ListItem>
                                )}
                                <ListItem>
                                    <ListItemText
                                        primary="Request ID"
                                        secondary={offerData.request_id}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Created Date"
                                        secondary={new Date(offerData.created_at).toLocaleString()}
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Custom Offer Services */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Included Services ({offerData.custom_offer_details.length})
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            {offerData.custom_offer_details.map((service, index) => (
                                <Accordion
                                    key={index}
                                    expanded={expandedService === index}
                                    onChange={() => handleServiceExpand(index, service, offerData.request_id)}
                                    sx={{ mb: 2 }}
                                >
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                                                {getServiceIcon(service.our_service_id)}
                                            </Avatar>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    {service.our_service_name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {service.title}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    ID: {service.id} • Service ID: {service.our_service_id}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                <Chip
                                                    label={service.status}
                                                    size="small"
                                                    color={service.status === 'complete' ? 'success' : service.status === 'active' ? 'primary' : 'default'}
                                                    sx={{ mb: 1 }}
                                                />
                                                <Typography variant="caption" color="text.secondary">
                                                    Click to expand
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>

                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={() => navigate(`/dashboard/services-offer-edit/${service.id}`)}
                                                    sx={{ ml: 2 }}
                                                >
                                                    Edit Service
                                                </Button>
                                            </Box>
                                        </Grid>
                                        {/* Service Information Section */}
                                        <Box sx={{ mb: 3 }}>
                                            {renderServiceInformation(service)}
                                        </Box>

                                        <Divider sx={{ my: 3 }} />

                                        {/* Service Specific Details */}
                                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Description sx={{ mr: 1 }} />
                                            Service Specific Details
                                        </Typography>

                                        <Grid container spacing={2} sx={{ mb: 3 }}>
                                            {getServiceDetails(service).map((detail, detailIndex) => (
                                                <Grid item xs={12} sm={6} md={4} key={detailIndex}>
                                                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                            {detail.label}
                                                        </Typography>
                                                        <Typography variant="body1" fontWeight="medium">
                                                            {detail.isLink ? (
                                                                <a
                                                                    href={detail.value}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    style={{ color: '#1976d2', textDecoration: 'none' }}
                                                                >
                                                                    {detail.value}
                                                                </a>
                                                            ) : (
                                                                detail.value || 'N/A'
                                                            )}
                                                        </Typography>
                                                    </Paper>
                                                </Grid>
                                            ))}
                                        </Grid>

                                        <Divider sx={{ my: 3 }} />

                                        {/* Statistics Section */}
                                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                            <BarChart sx={{ mr: 1 }} />
                                            Service Statistics & Analytics
                                        </Typography>
                                        {renderServiceStatistics(service)}
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    };

    const renderStaticOffer = () => {
        if (!offerData.offer) return null;

        return (
            <Grid container spacing={3}>
                {/* Offer Information */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                <Receipt sx={{ mr: 1 }} />
                                Offer Information
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <List dense>
                                <ListItem>
                                    <ListItemText
                                        primary="Offer Title"
                                        secondary={offerData.offer.title}
                                    />
                                </ListItem>
                                {offerData.offer.note && (
                                    <ListItem>
                                        <ListItemText
                                            primary="Note"
                                            secondary={offerData.offer.note}
                                        />
                                    </ListItem>
                                )}
                                <ListItem>
                                    <ListItemText
                                        primary="Description"
                                        secondary={offerData.description || 'No description'}
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Pricing Information */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                <Payment sx={{ mr: 1 }} />
                                Pricing
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell><strong>Total Price</strong></TableCell>
                                            <TableCell align="right">${offerData.offer.total_price}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell><strong>Discount</strong></TableCell>
                                            <TableCell align="right">
                                                {offerData.offer.discount_type === 'percentage'
                                                    ? `${offerData.offer.discount}%`
                                                    : offerData.offer.discount_type === 'const'
                                                        ? `$${offerData.offer.discount}`
                                                        : 'No discount'
                                                }
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell><strong>Final Price</strong></TableCell>
                                            <TableCell align="right">${offerData.offer.final_price}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Included Services */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Included Services
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            {offerData.offer.offer_details.map((service, index) => (
                                <Accordion key={index}>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                                                {getServiceIcon(service.our_service_id)}
                                            </Avatar>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography variant="subtitle1">
                                                    {service.our_service_name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {service.description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <Paper variant="outlined" sx={{ p: 2 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Subscription Days
                                                    </Typography>
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {service.days}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                            {service.count_ticket && (
                                                <Grid item xs={12} md={6}>
                                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                                        <Typography variant="subtitle2" color="text.secondary">
                                                            Ticket Count
                                                        </Typography>
                                                        <Typography variant="body1" fontWeight="medium">
                                                            {service.count_ticket}
                                                        </Typography>
                                                    </Paper>
                                                </Grid>
                                            )}
                                            {service.our_service_type && (
                                                <Grid item xs={12} md={6}>
                                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                                        <Typography variant="subtitle2" color="text.secondary">
                                                            Service Type
                                                        </Typography>
                                                        <Typography variant="body1" fontWeight="medium">
                                                            {service.our_service_type.type}
                                                        </Typography>
                                                    </Paper>
                                                </Grid>
                                            )}
                                            {service.our_service_type && (
                                                <Grid item xs={12} md={6}>
                                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                                        <Typography variant="subtitle2" color="text.secondary">
                                                            Service Price
                                                        </Typography>
                                                        <Typography variant="body1" fontWeight="medium">
                                                            ${service.our_service_type.price}
                                                        </Typography>
                                                    </Paper>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    };

    if (isLoading) {
        return (
            <Box sx={{ width: '100%', p: 3 }}>
                <LinearProgress />
                <Typography variant="h6" sx={{ mt: 2 }}>Loading offer details...</Typography>
            </Box>
        );
    }

    if (!offerData) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="error">
                    Offer not found or failed to load.
                </Typography>
                <Button variant="contained" onClick={fetchOfferData} sx={{ mt: 2 }}>
                    Retry
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
            {/* Header */}
            <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <CardContent>
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 60, height: 60 }}>
                                <Receipt />
                            </Avatar>
                        </Grid>
                        <Grid item xs>
                            <Typography variant="h4" fontWeight="bold">
                                {offerData.title || 'Offer Details'}
                            </Typography>
                            <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                {offerData.type === 'static_offer' ? 'Static Offer' : 'Custom Offer'} • Request ID: {offerData.request_id}
                            </Typography>
                        </Grid>
                        <Grid item>
                            {getStatusChip(offerData.status)}
                            <Box sx={{ mt: 1 }}>
                                {getStatusChip(offerData.status_payment)}
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Pricing Summary */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Order Summary
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="h6" color="primary">
                                    ${offerData.price}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Total Price
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="h6" color="secondary">
                                    {offerData.discount_type === 'percentage'
                                        ? `${offerData.discount}%`
                                        : offerData.discount_type === 'const'
                                            ? `$${offerData.discount}`
                                            : 'No discount'
                                    }
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Discount
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="h6" color="success.main">
                                    {new Date(offerData.created_at).toLocaleDateString()}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Created Date
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Offer Content */}
            {offerData.type === 'static_offer' ? renderStaticOffer() : renderCustomOffer()}

            {/* Action Buttons */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
                {offerData.status_payment === 'pending' && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => window.open(`/OffersInvoice?invoice_id=${offerData.uuid}`, '_blank')}
                    >
                        Pay Now
                    </Button>
                )}

                <Button
                    variant="outlined"
                    onClick={() => window.history.back()}
                >
                    Back to Orders
                </Button>
            </Box>
        </Box>
    );
};

export default OfferDetails;